import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router } from '@angular/router';
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  format,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { it } from 'date-fns/locale';
import { Observable, catchError, filter, forkJoin, map, of, take } from 'rxjs';

import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { FiscalEventInterface } from '@app/interface/fiscal-event';
import { InvoiceInterface } from '@app/interface/invoice';
import { PersonalEventInterface } from '@app/interface/personal-event';
import {
  FiscalEventDetailData,
  FiscalEventDetailsComponent,
} from '@app/fiscal-event-details/fiscal-event-details.component';
import {
  NewPersonalEventDialogComponent,
  NewPersonalEventDialogData,
} from '@app/new-personal-event-dialog/new-personal-event-dialog.component';
import {
  PlannerDayDetailsComponent,
  PlannerDayDetailsData,
  PlannerDayDetailsResult,
} from '@app/planner-day-details/planner-day-details.component';
import { FiscalEventApi } from '@app/services/fiscal-event-api.service';
import { InvoiceApi } from '@app/services/invoice-api';
import { PersonalEventApi } from '@app/services/personal-event-api.service';
import {
  httpQueryParams,
  PaginatedResponse,
} from '@app/services/base-authenticated.service';
import { ToastService } from '@app/services/toast.service';
import {
  BoardCategory,
  BoardItem,
  compareBoardItems,
  fiscalEventItem,
  invoiceIssuedItem,
  invoicePaymentItem,
  invoiceSettledPaymentItem,
  personalEventItem,
  toAmount,
} from './board-item';

/** Sei settimane fisse: cambiando mese la bacheca non cambia altezza. */
const GRID_DAYS = 42;
/** Oltre tre voci la cella diventa illeggibile: il resto va nel dettaglio. */
const MAX_ITEMS_PER_CELL = 3;
/** Quanti post-it di scadenza mostrare nella colonna di destra. */
const UPCOMING_COUNT = 4;
/** Tetto di pagina del backend (I3Pagination.max_page_size). */
const MAX_PAGE_SIZE = 100;

export interface PlannerCell {
  date: Date;
  dayKey: string;
  dayNumber: number;
  inMonth: boolean;
  today: boolean;
  weekend: boolean;
  items: BoardItem[];
  visibleItems: BoardItem[];
  hiddenCount: number;
  /** Totale lordo delle fatture emesse quel giorno, 0 se nessuna. */
  issuedTotal: number;
}

export type DeadlineUrgency = 'overdue' | 'urgent' | 'soon' | 'later';

/**
 * Post-it della colonna di destra. Tiene insieme scadenze fiscali e attività
 * personali: per chi legge sono la stessa cosa (qualcosa che sta arrivando),
 * cambia solo cosa succede al click.
 */
export interface DeadlineNote {
  /** Chiave stabile per `track`: gli id delle due risorse si sovrappongono. */
  key: string;
  kind: 'FISCAL' | 'PERSONAL';
  title: string;
  date: Date;
  label: string;
  urgency: DeadlineUrgency;
  amount?: number;
  icon: string;
  fiscalEvent?: FiscalEventInterface;
  personalEvent?: PersonalEventInterface;
}

interface FilterChip {
  category: BoardCategory;
  label: string;
  icon: string;
}

/**
 * Bacheca: calendario mensile a sinistra, post-it delle prossime scadenze a
 * destra.
 *
 * La griglia è costruita a mano con date-fns invece che con angular-calendar
 * perché le celle devono contenere tre tipi di contenuto eterogenei e seguire
 * il linguaggio visivo dell'app (bordo nero 2px, angolo tagliato, pillole),
 * cosa che il CSS della libreria non consente senza riscriverlo per intero.
 */
@Component({
  selector: 'izy-planner-board',
  standalone: true,
  imports: [
    CommonModule,
    BackHeaderComponent,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
  ],
  templateUrl: './planner-board.component.html',
  styleUrl: './planner-board.component.scss',
})
export class PlannerBoardComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private bottomSheet = inject(MatBottomSheet);
  private toast = inject(ToastService);
  private fiscalEventApi = inject(FiscalEventApi);
  private personalEventApi = inject(PersonalEventApi);
  private invoiceApi = inject(InvoiceApi);

  organizationId: string | null = null;

  readonly weekdays = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom'];

  readonly filterChips: FilterChip[] = [
    { category: 'FATTURE', label: 'Fatture', icon: 'receipt_long' },
    { category: 'FISCO', label: 'Fisco', icon: 'gavel' },
    { category: 'ATTIVITA', label: 'Attività', icon: 'checklist' },
  ];

  readonly loading = signal(true);
  readonly viewDate = signal(startOfMonth(new Date()));
  readonly filters = signal<Record<BoardCategory, boolean>>({
    FATTURE: true,
    FISCO: true,
    ATTIVITA: true,
  });

  private readonly fiscalEvents = signal<FiscalEventInterface[]>([]);
  private readonly personalEvents = signal<PersonalEventInterface[]>([]);
  private readonly invoicesIssued = signal<InvoiceInterface[]>([]);
  private readonly invoicesPayments = signal<InvoiceInterface[]>([]);
  private readonly invoicesSettled = signal<InvoiceInterface[]>([]);
  readonly upcoming = signal<DeadlineNote[]>([]);

  /** Lunedì della settimana in cui cade il primo del mese visualizzato. */
  private readonly gridStart = computed(() =>
    startOfWeek(startOfMonth(this.viewDate()), { weekStartsOn: 1 }),
  );

  readonly monthLabel = computed(() =>
    format(this.viewDate(), 'LLLL yyyy', { locale: it }),
  );

  /** Tutte le voci del periodo visualizzato, raggruppate per giorno. */
  private readonly itemsByDay = computed(() => {
    const active = this.filters();
    const byDay = new Map<string, BoardItem[]>();

    const push = (item: BoardItem | null): void => {
      if (!item || !active[item.category]) {
        return;
      }
      const list = byDay.get(item.dayKey);
      if (list) {
        list.push(item);
      } else {
        byDay.set(item.dayKey, [item]);
      }
    };

    for (const invoice of this.invoicesIssued()) {
      push(invoiceIssuedItem(invoice));
    }
    for (const invoice of this.invoicesPayments()) {
      push(invoicePaymentItem(invoice));
    }
    for (const invoice of this.invoicesSettled()) {
      push(invoiceSettledPaymentItem(invoice));
    }
    for (const event of this.fiscalEvents()) {
      push(fiscalEventItem(event));
    }
    for (const event of this.personalEvents()) {
      push(personalEventItem(event));
    }

    for (const list of byDay.values()) {
      list.sort(compareBoardItems);
    }
    return byDay;
  });

  readonly cells = computed<PlannerCell[]>(() => {
    const start = this.gridStart();
    const month = this.viewDate();
    const byDay = this.itemsByDay();
    const cells: PlannerCell[] = [];

    for (let offset = 0; offset < GRID_DAYS; offset++) {
      const date = addDays(start, offset);
      const dayKey = format(date, 'yyyy-MM-dd');
      const items = byDay.get(dayKey) ?? [];
      const weekday = date.getDay();

      cells.push({
        date,
        dayKey,
        dayNumber: date.getDate(),
        inMonth: isSameMonth(date, month),
        today: isToday(date),
        weekend: weekday === 0 || weekday === 6,
        items,
        visibleItems: items.slice(0, MAX_ITEMS_PER_CELL),
        hiddenCount: Math.max(0, items.length - MAX_ITEMS_PER_CELL),
        issuedTotal: items
          .filter((item) => item.kind === 'INVOICE_ISSUED')
          .reduce((sum, item) => sum + (item.amount ?? 0), 0),
      });
    }

    return cells;
  });

  /** Vista alternativa per mobile: solo i giorni del mese che hanno qualcosa. */
  readonly agendaDays = computed(() =>
    this.cells().filter((cell) => cell.inMonth && cell.items.length > 0),
  );

  readonly isEmptyMonth = computed(
    () => !this.loading() && this.agendaDays().length === 0,
  );

  ngOnInit(): void {
    this.organizationId = this.route.snapshot.paramMap.get('organizationId');
    if (!this.organizationId) {
      return;
    }
    this.loadMonth();
    this.loadUpcoming();
  }

  // ── Navigazione ────────────────────────────────────────────────────────

  previousMonth(): void {
    this.viewDate.set(subMonths(this.viewDate(), 1));
    this.loadMonth();
  }

  nextMonth(): void {
    this.viewDate.set(addMonths(this.viewDate(), 1));
    this.loadMonth();
  }

  goToToday(): void {
    const current = startOfMonth(new Date());
    if (isSameMonth(current, this.viewDate())) {
      return;
    }
    this.viewDate.set(current);
    this.loadMonth();
  }

  backToDashboard(): void {
    this.router.navigate([`/${this.organizationId}/dashboard`]);
  }

  goToFiscalPlan(): void {
    this.router.navigate([`/${this.organizationId}/fiscal_plan`]);
  }

  // ── Filtri ─────────────────────────────────────────────────────────────

  toggleFilter(category: BoardCategory): void {
    this.filters.update((current) => ({
      ...current,
      [category]: !current[category],
    }));
  }

  isFilterActive(category: BoardCategory): boolean {
    return this.filters()[category];
  }

  // ── Interazioni ────────────────────────────────────────────────────────

  openDay(cell: PlannerCell): void {
    const organizationId = Number(this.organizationId);
    const data: PlannerDayDetailsData = {
      date: cell.date,
      items: cell.items,
      organizationId,
    };

    this.bottomSheet
      .open(PlannerDayDetailsComponent, {
        data,
        panelClass: 'planner-day-bottom-sheet',
      })
      .afterDismissed()
      .subscribe((result: PlannerDayDetailsResult) => {
        if (result === 'changed') {
          this.loadMonth();
          this.loadUpcoming();
        }
      });
  }

  /** Il post-it porta al dettaglio della scadenza o alla modifica dell'attività. */
  openDeadline(note: DeadlineNote): void {
    if (note.personalEvent) {
      this.dialog
        .open(NewPersonalEventDialogComponent, {
          width: '90%',
          maxWidth: '450px',
          autoFocus: false,
          panelClass: 'izy-dialog-panel',
          data: {
            organizationId: Number(this.organizationId),
            event: note.personalEvent,
          } satisfies NewPersonalEventDialogData,
        })
        .afterClosed()
        .subscribe((result) => {
          if (result === 'success') {
            this.loadMonth();
            this.loadUpcoming();
          }
        });
      return;
    }

    if (!note.fiscalEvent) {
      return;
    }

    const data: FiscalEventDetailData = {
      fiscalEvent: note.fiscalEvent,
      organization: null,
    };
    this.bottomSheet.open(FiscalEventDetailsComponent, {
      data,
      panelClass: 'fiscal-event-detail-bottom-sheet',
    });
  }

  addActivity(date?: Date): void {
    const data: NewPersonalEventDialogData = {
      organizationId: Number(this.organizationId),
      date: date ?? new Date(),
    };

    this.dialog
      .open(NewPersonalEventDialogComponent, {
        width: '90%',
        maxWidth: '450px',
        autoFocus: false,
        panelClass: 'izy-dialog-panel',
        data,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'success') {
          this.loadMonth();
          this.loadUpcoming();
        }
      });
  }

  // ── Caricamento dati ───────────────────────────────────────────────────

  /**
   * Ricarica l'intera finestra visibile (le 6 settimane della griglia, non il
   * solo mese) in modo che anche i giorni di spillover mostrino il contenuto.
   */
  /**
   * Scadenze fiscali e attività personali vengono caricate in una singola
   * pagina da `MAX_PAGE_SIZE`: se un'organizzazione ne ha di più nella
   * finestra richiesta, il resto va perso silenziosamente. Qui almeno si
   * avvisa, invece di far credere che la bacheca sia completa.
   */
  private extractResults<T>(page: PaginatedResponse<T>, label: string): T[] {
    if (page.count > page.results.length) {
      console.warn(
        `Bacheca: "${label}" superano il limite di pagina (${page.results.length}/${page.count} mostrati)`,
      );
      this.toast.error(
        `Non tutte le "${label}" sono visibili in bacheca: sono più di ${page.results.length}`,
      );
    }
    return page.results;
  }

  private loadMonth(): void {
    const organizationId = this.organizationId;
    if (!organizationId) {
      return;
    }

    const start = this.gridStart();
    const from = format(start, 'yyyy-MM-dd');
    const to = format(addDays(start, GRID_DAYS - 1), 'yyyy-MM-dd');
    // `marked_paid_at` è un datetime: confrontarlo con `__lte: to` taglierebbe
    // fuori tutto ciò che è stato incassato dopo la mezzanotte dell'ultimo
    // giorno, quindi si usa il limite aperto sul giorno successivo.
    const beforeNextDay = format(addDays(start, GRID_DAYS), 'yyyy-MM-dd');

    this.loading.set(true);

    const invoiceBase: httpQueryParams = {
      is_draft: false,
      is_deleted: false,
      is_preventive: false,
    };

    forkJoin({
      fiscal: this.fiscalEventApi
        .getEventList<FiscalEventInterface>(organizationId, {
          date__gte: from,
          date__lte: to,
          is_deleted: false,
          page_size: MAX_PAGE_SIZE,
        })
        .pipe(
          map((page) => this.extractResults(page, 'scadenze fiscali')),
          catchError((err) => {
            console.error('Errore caricamento scadenze fiscali:', err);
            return of<FiscalEventInterface[]>([]);
          }),
        ),
      personal: this.personalEventApi
        .getEventList<PersonalEventInterface>(organizationId, {
          date__gte: from,
          date__lte: to,
          is_deleted: false,
          page_size: MAX_PAGE_SIZE,
        })
        .pipe(
          map((page) => this.extractResults(page, 'attività')),
          catchError((err) => {
            console.error('Errore caricamento attività:', err);
            return of<PersonalEventInterface[]>([]);
          }),
        ),
      issued: this.loadInvoices({
        ...invoiceBase,
        emission_date__gte: from,
        emission_date__lte: to,
      }),
      payments: this.loadInvoices({
        ...invoiceBase,
        is_paid: false,
        payment_date__gte: from,
        payment_date__lte: to,
      }),
      settled: this.loadInvoices({
        ...invoiceBase,
        is_paid: true,
        marked_paid_at__gte: from,
        marked_paid_at__lt: beforeNextDay,
      }),
    }).subscribe({
      next: ({ fiscal, personal, issued, payments, settled }) => {
        this.fiscalEvents.set(fiscal);
        this.personalEvents.set(personal);
        this.invoicesIssued.set(issued);
        this.invoicesPayments.set(payments);
        this.invoicesSettled.set(settled);
        this.loading.set(false);
      },
      error: (err: unknown) => {
        console.error('Errore caricamento bacheca:', err);
        this.loading.set(false);
        this.toast.error('Non è stato possibile caricare la bacheca');
      },
    });
  }

  /**
   * Le fatture usano il caricamento ricorsivo perché un mese può superare la
   * pagina da 100: `take(1)` sull'emissione completa chiude il Subject, che
   * altrimenti non terminerebbe mai e bloccherebbe la forkJoin.
   */
  private loadInvoices(params: httpQueryParams): Observable<InvoiceInterface[]> {
    return this.invoiceApi
      .getAllInvoices<InvoiceInterface>(Number(this.organizationId), params)
      .pipe(
        filter((page) => page.completed),
        take(1),
        map((page) => page.results),
        catchError((err) => {
          console.error('Errore caricamento fatture:', err);
          return of<InvoiceInterface[]>([]);
        }),
      );
  }

  /**
   * I post-it non seguono il mese visualizzato: mostrano sempre le prossime
   * scadenze reali a partire da oggi, altrimenti scorrendo i mesi passati si
   * perderebbe il richiamo alle cose da pagare.
   *
   * Scadenze fiscali e attività personali vengono unite e ordinate insieme:
   * la colonna risponde alla domanda "cosa mi aspetta", non "cosa devo pagare".
   *
   * `is_paid` / `is_done` non sono filtrabili lato backend e non esiste
   * `ordering` su queste risorse: si filtra e si ordina qui.
   */
  private loadUpcoming(): void {
    const organizationId = this.organizationId;
    if (!organizationId) {
      return;
    }

    const today = new Date();
    const from = format(today, 'yyyy-MM-dd');

    forkJoin({
      fiscal: this.fiscalEventApi
        .getEventList<FiscalEventInterface>(organizationId, {
          date__gte: from,
          is_deleted: false,
          page_size: MAX_PAGE_SIZE,
        })
        .pipe(
          map((page) => this.extractResults(page, 'prossime scadenze')),
          catchError((err) => {
            console.error('Errore caricamento prossime scadenze:', err);
            return of<FiscalEventInterface[]>([]);
          }),
        ),
      personal: this.personalEventApi
        .getEventList<PersonalEventInterface>(organizationId, {
          date__gte: from,
          is_deleted: false,
          page_size: MAX_PAGE_SIZE,
        })
        .pipe(
          map((page) => this.extractResults(page, 'prossime attività')),
          catchError((err) => {
            console.error('Errore caricamento prossime attività:', err);
            return of<PersonalEventInterface[]>([]);
          }),
        ),
    }).subscribe({
      next: ({ fiscal, personal }) => {
        const notes = [
          ...fiscal
            .filter((event) => !event.is_paid && !event.is_credit)
            .map((event) => this.fiscalNote(event, today)),
          ...personal
            .filter((event) => !event.is_done)
            .map((event) => this.personalNote(event, today)),
        ]
          .sort((a, b) => a.date.getTime() - b.date.getTime())
          .slice(0, UPCOMING_COUNT);

        this.upcoming.set(notes);
      },
      error: (err: unknown) => {
        console.error('Errore caricamento prossime scadenze:', err);
        this.upcoming.set([]);
      },
    });
  }

  private fiscalNote(event: FiscalEventInterface, today: Date): DeadlineNote {
    const date = this.toLocalDate(event.date);
    const daysLeft = differenceInCalendarDays(date, today);

    return {
      key: `FISCAL-${event.id}`,
      kind: 'FISCAL',
      title: event.name,
      date,
      label: this.countdownLabel(daysLeft),
      urgency: this.urgencyOf(daysLeft),
      amount: toAmount(event.amount),
      icon: 'gavel',
      fiscalEvent: event,
    };
  }

  private personalNote(
    event: PersonalEventInterface,
    today: Date,
  ): DeadlineNote {
    const date = this.toLocalDate(event.date);
    const daysLeft = differenceInCalendarDays(date, today);

    return {
      key: `PERSONAL-${event.id}`,
      kind: 'PERSONAL',
      title: event.title,
      date,
      label: this.countdownLabel(daysLeft),
      urgency: this.urgencyOf(daysLeft),
      icon: event.is_important ? 'push_pin' : 'checklist',
      personalEvent: event,
    };
  }

  /**
   * Le DateField del backend arrivano come 'YYYY-MM-DD': costruire il Date dai
   * pezzi evita che vengano lette come UTC e retrocedano di un giorno.
   */
  private toLocalDate(value: string | Date): Date {
    const raw = typeof value === 'string' ? value.slice(0, 10) : null;
    return raw
      ? new Date(
          Number(raw.slice(0, 4)),
          Number(raw.slice(5, 7)) - 1,
          Number(raw.slice(8, 10)),
        )
      : new Date(value);
  }

  private countdownLabel(daysLeft: number): string {
    if (daysLeft < 0) {
      const days = Math.abs(daysLeft);
      return days === 1 ? 'Scaduta da 1 giorno' : `Scaduta da ${days} giorni`;
    }
    if (daysLeft === 0) {
      return 'Scade oggi';
    }
    if (daysLeft === 1) {
      return 'Scade domani';
    }
    return `Tra ${daysLeft} giorni`;
  }

  private urgencyOf(daysLeft: number): DeadlineUrgency {
    if (daysLeft < 0) {
      return 'overdue';
    }
    if (daysLeft <= 7) {
      return 'urgent';
    }
    if (daysLeft <= 15) {
      return 'soon';
    }
    return 'later';
  }
}
